import { parseDate } from "../Helpers";
import { AppointmentsApi } from "../../Api/AppointmentsApi";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import { AppointmentsAvailabilityDto } from "../../../../Api/Model/AppointmentsAvailabilityDto.csharp";
import { FormStateType } from "../Helpers/formTypes";
import { GtmService } from "../GtmService";
import { useBookingContext } from "../../BookingButton/BookingContext/BookingContext";
import { IntervalDay } from "../Steps/FormSecondStep";
import { CenterDto } from "@apiModels/CenterDto.csharp";
import { filterAppointments } from "../Helpers/filterAppointments";
import { newDateInOsloTimeZone } from "../Helpers/timeZoneHelpers";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";

const appointmentApi = new AppointmentsApi();

export const useInterval = (
  state: FormStateType,
  selectedMonth: Date,
  autoQueryNextMonth: () => void,
  autoQueryRepeat: number,
  filteredDoctors: DoctorDto[]
) => {
  const [interval, setInterval] = useState<IntervalDay[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<
    AppointmentsAvailabilityDto[]
  >([]);
  const context = useBookingContext();

  const { appointments, loadingAppointments, error, warning, failedCenters } =
    useAppointments(
      state,
      selectedMonth,
      autoQueryNextMonth,
      context.dipsAutoQueryMaxRepeats,
      autoQueryRepeat,
      filteredDoctors
    );

  const getInterval = (appointments: AppointmentsAvailabilityDto[]) => {
    if (!appointments.length) {
      return [];
    }

    return eachDayOfInterval({
      start: appointments[0].date,
      end: appointments[appointments.length - 1].date,
    }).map<IntervalDay>((date) => {
      return {
        date,
        hasAppointments: appointments.some(
          (a) => startOfDay(a.date).getTime() === date.getTime()
        ),
      };
    });
  };

  useEffect(() => {
    const filteredAppointments = filterAppointments(
      state,
      appointments,
      filteredDoctors
    );
    setFilteredAppointments(filteredAppointments);
    setInterval(getInterval(filteredAppointments));
  }, [
    state.doctor,
    state.location,
    appointments,
    state.relatedServiceSetups,
    state.showRelatedServicesMap,
  ]);

  return {
    interval,
    filteredAppointments,
    loadingAppointments,
    autoQueryRepeat,
    error,
    warning,
    failedCenters,
  };
};

const useAppointments = (
  state: FormStateType,
  selectedMonth: Date,
  autoQueryNextMonth: () => void,
  dipsAutoQueryMaxRepeats: number,
  autoQueryRepeat: number,
  filteredDoctors: DoctorDto[]
) => {
  const [appointments, setAppointments] =
    useState<AppointmentsAvailabilityDto[]>();
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [failedCenters, setFailedCenters] = useState<CenterDto[]>([]);

  const tryFetchAppointments = async () => {
    const savedAppointments =
      appointments?.filter(
        (a) => startOfMonth(a.date).getTime() == selectedMonth.getTime()
      ) ?? [];
    if (savedAppointments.length == 0) {
      if (loadingAppointments && autoQueryRepeat === 0) return;
      setLoadingAppointments(true);
      try {
        const { startDate, endDate } = getPeriod(selectedMonth);
        const response = await getAppointments(
          state.service.id,
          startDate,
          endDate
        );
        if (response) {
          setWarning(response.cannotLoadSomeAppointments);
          setFailedCenters(response.failedCenters);
          const newAppointments = [
            ...(appointments ?? []),
            ...response.appointments,
          ];
          setAppointments(newAppointments);
          setLoadingAppointments(false);
          return {
            appointments: newAppointments,
            failedCenters: response.failedCenters,
          };
        }
      } catch (err: any) {
        setError(err.message);
        GtmService.appointmentsLoadingError(
          state.service.name,
          state.specialization.name,
          state.location?.name,
          err.message
        );
      }
    }

    setLoadingAppointments(false);
    return {
      appointments,
      failedCenters,
    };
  };

  useEffect(() => {
    if (selectedMonth) {
      (async () => {
        const fetchedAppointments = await tryFetchAppointments();
        const filtered = filterAppointments(
          state,
          fetchedAppointments?.appointments,
          filteredDoctors
        );
        if (
          !filtered.length &&
          dipsAutoQueryMaxRepeats > autoQueryRepeat &&
          fetchedAppointments?.failedCenters.every(
            (c) => c.id !== state.location?.id
          )
        ) {
          autoQueryNextMonth();
        }
      })();
    }
  }, [selectedMonth]);

  return {
    appointments,
    loadingAppointments,
    autoQueryRepeat,
    error,
    warning,
    failedCenters,
  };
};

const getAppointments = async (
  serviceId: string,
  startDate: Date,
  endDate: Date
) => {
  const response = await appointmentApi.getAvailable(
    serviceId,
    parseDate(startDate),
    parseDate(endDate)
  );

  return {
    cannotLoadSomeAppointments: response.cannotLoadSomeAppointments,
    failedCenters: response.failedCenters,
    appointments: response.appointments.map((a) => ({
      ...a,
      date: new Date(a.date),
    })),
  };
};

const getPeriod = (selectedMonth: Date) => {
  const startDate =
    selectedMonth.getTime() > newDateInOsloTimeZone().getTime()
      ? startOfMonth(selectedMonth)
      : startOfDay(newDateInOsloTimeZone());

  const endDate = endOfMonth(startDate);

  return { startDate, endDate };
};
